/**
 * SONE web — page view.
 *
 * Live-synced title plus a read-only block rendering. The title is editable
 * because it demonstrates the whole write path end to end — type here and the
 * sidebar, the search index and the other browser all follow.
 */

import type { PageHandle } from '@sone/client';

import type { PageNode } from '../api/client.ts';
import {
  DOC_KEYS,
  PAGE_KEYS,
  readEntryCover,
  readEntryIcon,
  readTitleColor,
  type EntryCover,
  type EntryIcon,
  type PdfPlace,
} from '@sone/core';
import { useEntryKind } from '../hooks/usePageWidth.ts';
import { usePageWidth } from '../hooks/usePageWidth.ts';
import { useT } from '../i18n/useT.tsx';
import { paths } from '../routes/paths.ts';
import { usePageLink } from '../routes/pageLink.tsx';

import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { blockFromHash } from '../routes/paths.ts';
import { useLocationHash } from '../hooks/useRoute.ts';
import { scrollToBlock } from '../hooks/useOutline.ts';
import type { CommentAnchor, DrawnThread } from '@sone/editor';

import { CanvasSurface } from './CanvasSurface.tsx';
import { VersionDiff } from './VersionDiff.tsx';
import { VersionView } from './VersionView.tsx';
import { EditorSurface } from './EditorSurface.tsx';
import type { LinkablePage } from './PageLinkMenu.tsx';
import { Breadcrumb } from './Breadcrumb.tsx';
import { EntryCoverHead } from './EntryCover.tsx';
import { EntryIconView, titleColorStyle } from './EntryIconView.tsx';
import { ErrorBoundary } from './ErrorBoundary.tsx';

interface PageViewProps {
  /** So a refusal is only believed once the connection has settled. */
  connectionState: string;
  handle: PageHandle;
  pageId: string;
  /** The threads to mark in the text, read by the workspace (ADR-0046). */
  threads: DrawnThread[];
  /**
   * The threads themselves, for the canvas's marks (ADR-0057).
   *
   * Separate from `threads` above, which is the editor's drawn shape and holds
   * a text range rather than an item — `DrawnThread` has no `item`, which I
   * found by writing against a field it does not have.
   */
  itemThreads?: Array<{ item: string | null; resolved: boolean }>;
  internalItemThreads?: Array<{ item: string | null; resolved: boolean }>;
  /** A selection somebody wants to comment on. */
  onComment: (anchor: CommentAnchor) => void;
  /** A place in a PDF somebody wants to mark, saying nothing about it (ADR-0152). */
  onMark: (place: PdfPlace, quote: string) => void;
  /** And one they want to take off again. */
  onUnmark: (markId: string) => void;
  /** Whether the highlighter is offered — see `EditorSurfaceProps`. */
  mayMark: boolean;
  /** How much to mark a commented passage (ADR-0046). */
  markStyle: 'highlight' | 'underline' | 'off';
  /**
   * The folders above this page, when the sidebar is not showing them.
   *
   * Empty when it is: the sidebar is the trail, and two of them is one too
   * many.
   */
  trail: PageNode[];
  /** The workspace's people, for assigning a task (ADR-0052). */
  members: Array<{ userId: string; displayName: string }>;
  /** What `[[` may link to. Empty on a share link (ADR-0173). */
  linkablePages: readonly LinkablePage[];
  /** A past version to show instead of the body (ADR-0047). */
  viewingVersion: string | null;
  /** Or what that version changed (ADR-0053). */
  comparingVersion: string | null;
  onCloseVersion: () => void;
  /**
   * Called when the title changes, including by another client.
   *
   * The sidebar reads titles from the projection over HTTP, so without this it
   * shows the old name until something refetches — which is what made editing a
   * heading look like it had not synced.
   */
  onTitleChange?: (title: string) => void;
}

/** An entry's icon and title colour out of whatever the document holds. */
function readIcon(value: unknown): { icon: EntryIcon | null; titleColor: string | null } {
  return { icon: readEntryIcon(value), titleColor: readTitleColor(value) };
}

export function PageView({
  handle,
  threads,
  itemThreads,
  internalItemThreads,
  onComment,
  onMark,
  onUnmark,
  mayMark,
  markStyle,
  trail,
  members,
  linkablePages,
  viewingVersion,
  comparingVersion,
  onCloseVersion,
  pageId,
  connectionState,
  onTitleChange,
}: PageViewProps): ReactElement {
  const pageLink = usePageLink();
  const isCanvas = useEntryKind(handle?.doc ?? null) === 'canvas';
  // From the document, so it arrives like any other edit (ADR-0028).
  const width = usePageWidth(handle?.doc ?? null);
  const { t } = useT();

  /*
   * Which canvas items carry comments, and how many of those are internal
   * (ADR-0057).
   *
   * One map from both documents, built here because the canvas draws items and
   * knows nothing about threads — handing it two lists to reconcile would put
   * the distinction in a third place, and this is the one distinction where
   * being wrong is a disclosure.
   */
  const commentedItems = useMemo(() => {
    const counts = new Map<string, { total: number; internal: number }>();
    const add = (
      list: Array<{ item: string | null; resolved: boolean }>,
      internal: boolean,
    ): void => {
      for (const thread of list) {
        // Only item threads: a text thread's mark is the editor's business.
        if (!thread.item || thread.resolved) continue;
        const at = counts.get(thread.item) ?? { total: 0, internal: 0 };
        at.total += 1;
        if (internal) at.internal += 1;
        counts.set(thread.item, at);
      }
    };
    add(itemThreads ?? [], false);
    add(internalItemThreads ?? [], true);
    return counts;
  }, [itemThreads, internalItemThreads]);
  const pageMap = handle.doc.getMap(DOC_KEYS.page);
  const [title, setTitle] = useState<string>(
    () => (pageMap.get(PAGE_KEYS.title) as string | undefined) ?? '',
  );
  /** The fragment, so landing on a block re-runs when only that changed. */
  const hash = useLocationHash();

  /**
   * The page's own icon and colours (ADR-0030), read from the document.
   *
   * From the document rather than passed in from the tree: this component is
   * also what a share link renders, where there is no tree at all — and reading
   * it here means a change made in another browser arrives the same way a
   * rename does, through the observer below.
   *
   * The title colour is stored inside the same object, so both come from one
   * read.
   */
  const [icon, setIcon] = useState<{
    icon: EntryIcon | null;
    titleColor: string | null;
  }>(() => readIcon(pageMap.get(PAGE_KEYS.icon)));

  /**
   * The cover, from the document like the icon and for the same reasons
   * (ADR-0117).
   *
   * Written straight into the document rather than through the route a folder
   * uses, for the reason the title beside it does: this component has the
   * document in hand, and two neighbouring fields on one heading should not
   * behave differently. The route would work — the update bus carries an HTTP
   * write into an open room (ADR-0076) — at the cost of a rematerialise and a
   * tree refetch per click.
   */
  const [cover, setCover] = useState<EntryCover | null>(() =>
    readEntryCover(pageMap.get(PAGE_KEYS.cover)),
  );

  // Mirror remote title changes into local state. Guarded against writing back
  // what we just typed, which would fight the cursor.
  useEffect(() => {
    const observer = (): void => {
      const next = (pageMap.get(PAGE_KEYS.title) as string | undefined) ?? '';
      setTitle((current) => (current === next ? current : next));
      // The same observer, because it fires for every key in the map and an
      // icon chosen elsewhere has to arrive here too. Compared before storing,
      // or every keystroke in the title would replace an equal object and
      // re-render the heading.
      setIcon((current) => {
        const read = readIcon(pageMap.get(PAGE_KEYS.icon));
        return current.icon?.value === read.icon?.value &&
          current.icon?.color === read.icon?.color &&
          current.titleColor === read.titleColor
          ? current
          : read;
      });
      // The same observer again, and compared the same way: the map fires for
      // every key, and a cover chosen in another browser has to arrive here.
      setCover((current) => {
        const read = readEntryCover(pageMap.get(PAGE_KEYS.cover));
        return JSON.stringify(current) === JSON.stringify(read) ? current : read;
      });
      // Reported for remote changes too, so a rename from another client
      // reaches the sidebar without a refetch.
      onTitleChange?.(next);
    };
    pageMap.observe(observer);
    return () => pageMap.unobserve(observer);
  }, [pageMap, onTitleChange]);

  /**
   * Land on the block a URL names (ADR-0033).
   *
   * A search result links to `#b-<id>`, and the block it names is not in the DOM
   * when the page mounts: the document arrives over the sync connection a moment
   * later. So this retries for a couple of seconds and then gives up quietly —
   * the alternative is a link that works on a fast connection and silently does
   * nothing on a slow one.
   *
   * Given up on rather than reported: the page is open and correct, and an error
   * about a failed scroll would be about a convenience.
   */
  useEffect(() => {
    const wanted = blockFromHash(hash);
    if (!wanted) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryScroll = (): void => {
      if (scrollToBlock(wanted)) return;
      attempts += 1;
      if (attempts > 20) return;
      timer = setTimeout(tryScroll, 100);
    };
    tryScroll();

    return () => {
      if (timer) clearTimeout(timer);
    };
    /*
     * The fragment is in the list, and it was not (ADR-0170).
     *
     * The note here already said *"following a second result from the same
     * search has to scroll again, and the hash is what changed"* — beside a
     * dependency list holding only the page, which does not change when the
     * fragment does. **A comment describing behaviour the dependency list
     * cannot produce.** So the second search result never scrolled, and neither
     * would a link to a block on the page you are already reading, which is the
     * ordinary case for linking one passage to another.
     */
  }, [pageId, hash]);

  const commitTitle = (next: string): void => {
    setTitle(next);
    if (!handle.canEdit) return;
    pageMap.set(PAGE_KEYS.title, next);
    // Told directly rather than waiting for the observer: a local write does
    // fire it, but going straight there keeps the sidebar in step with the
    // caret rather than one tick behind.
    onTitleChange?.(next);
  };

  return (
    <div className="page-body" data-width={width} data-kind={isCanvas ? 'canvas' : undefined}>
      {/* The cover, and the heading it sits above, in one hover region
        * (ADR-0117). The control to add one appears on hovering the heading,
        * which is why the two are wrapped rather than stacked. */}
      <EntryCoverHead
        cover={cover}
        pageId={pageId}
        {...(handle.canEdit
          ? {
              onChange: (next: EntryCover | null) => {
                setCover(next);
                if (next) pageMap.set(PAGE_KEYS.cover, next);
                else pageMap.delete(PAGE_KEYS.cover);
              },
            }
          : {})}
      >
        {/* Where this page sits, when the sidebar is not saying so — under the
          * cover and above the title, which is where a location belongs:
          * reading downwards gives the folders and then the page.
          *
          * Inside the heading region rather than above the cover (ADR-0164). A
          * cover that runs to the top edge is only at the top when it is the
          * first thing in the body, and a trail above it pushed the picture
          * down by its own height. */}
        <Breadcrumb trail={trail} />

        {/* The icon and the name on one line, the same shape a folder has. */}
        <div className="entry-heading">
          <span className="entry-heading-icon">
            {/* The page's own kind, not "page" written into the markup. This is
                the fourth place that hardcoded it, and the reason a canvas
                showed a sheet on its own heading while showing a brush
                everywhere else. */}
            <EntryIconView icon={icon.icon} kind={isCanvas ? 'canvas' : 'page'} />
          </span>
          <input
            className="page-title"
            style={titleColorStyle(icon.titleColor ? { titleColor: icon.titleColor } : null)}
            value={title}
            onChange={(e) => commitTitle(e.target.value)}
            placeholder={t('page.untitled')}
            readOnly={!handle.canEdit}
            aria-label={t('page.title')}
          />
        </div>
      </EntryCoverHead>

      {/* Three states, told apart on purpose.
       *
       * `canEdit` is false whenever the role is unknown, which includes while
       * the document is opening and while a reconnect is in flight — the role
       * is cleared when a connection drops and only returns with the next open
       * acknowledgement. Deriving "read-only" from it therefore told the owner
       * of a workspace they had no write access, mid-reconnect, which is both
       * alarming and false.
       *
       * Read-only is claimed only when the role is actually known and actually
       * read-only. */}
      {/* Denied, and the connection settled enough to believe it.
        *
        * Switching workspaces reconnects, and a page opened against the old
        * connection is refused — correctly, and for a page in the workspace
        * somebody has just arrived in. The refusal was true of a moment that
        * had already passed, and it was shown to somebody who had only pressed
        * a switcher.
        *
        * A page that is really out of reach still says so: the connection
        * becomes ready and the denial stands. */}
      {handle.status === 'denied' && connectionState === 'ready' && (
        <p className="error">{t('page.noAccess')}</p>
      )}
      {(handle.role === null || (handle.status === 'denied' && connectionState !== 'ready')) && (
        <p className="muted">{t('page.opening')}</p>
      )}
      {handle.status !== 'denied' && handle.role !== null && !handle.canEdit && (
        <p className="muted">{t('page.readOnly')}</p>
      )}

      {/* A second boundary around the editor specifically, so a crash there
          leaves the title, the sidebar and navigation working. Losing the
          editor is bad; losing the way out of the page is worse. */}
      {/* A canvas is a page with a different body, not a different screen: the
          title, the panel, the trail and the sharing are all the page's
          (ADR-0043). Only what is under the heading changes. */}
      {/* A past version, in place of the body (ADR-0047).
        *
        * In the content rather than in the panel: the point is to read the page
        * as it was, and a page cannot be read in a column beside itself. The
        * editor is not rendered at all rather than disabled — an editor that
        * refuses keystrokes is an invitation somebody has already accepted by
        * the time it refuses. */}
      {comparingVersion ? (
        <VersionDiff pageId={pageId} versionId={comparingVersion} onClose={onCloseVersion} />
      ) : viewingVersion ? (
        <VersionView pageId={pageId} versionId={viewingVersion} onClose={onCloseVersion} />
      ) : (
      <ErrorBoundary where="The editor">
        {isCanvas ? (
          <CanvasSurface
            handle={handle}
            pageId={pageId}
            canEdit={handle.canEdit !== false}
            // The panel takes it from here: a pending anchor carrying an item
            // rather than a text range, which is the shape the prose side
            // already hands over (ADR-0046).
            /*
             * Which items carry comments, counted from both documents
             * (ADR-0057).
             *
             * Built here rather than in the canvas: the canvas draws items and
             * knows nothing about threads, and handing it two lists to reconcile
             * would put the internal/ordinary distinction in a third place.
             */
            commented={commentedItems}
            onCommentItem={(itemId) =>
              onComment({ from: new Uint8Array(), to: new Uint8Array(), quote: '', item: itemId })
            }
          />
        ) : (
          <EditorSurface
            handle={handle}
            pageId={pageId}
            // For the address of a block, which wants a slug to be readable by
            // whoever pastes it (ADR-0170). Decorative, as always.
            pageTitle={title}
            linkablePages={linkablePages}
            threads={threads}
            members={members}
            onComment={onComment}
            onMark={onMark}
            onUnmark={onUnmark}
            mayMark={mayMark}
            markStyle={markStyle}
          />
        )}
      </ErrorBoundary>
      )}

    </div>
  );
}


/**
 * Connection state and who else is here.
 *
 * The wording matters more than it looks. An indefinite "Syncing…" was shown
 * for a connection that had never been established, which suggests progress
 * that is not happening and hides the one fact somebody could act on. When the
 * client reports why it cannot connect, that is said instead.
 */
export function PageStatus({ handle, connectionState, failure }: {
  handle: PageHandle | null;
  connectionState: string;
  failure?: { kind: string; attempts: number } | null;
}): ReactElement {
  const { t } = useT();
  const peers = handle?.peers() ?? [];

  // 'ready' plus a synced document is the only fully-good state; anything else
  // is reported rather than hidden, because a silent offline state is how
  // people lose work they thought was saved.
  const dot =
    connectionState === 'ready'
      ? handle && handle.status !== 'synced'
        ? 'offline'
        : 'ready'
      : connectionState === 'closed'
        ? 'closed'
        : 'offline';

  // A repeated failure is reported plainly rather than as another "…". Two
  // attempts is the threshold: one can be a page load racing the network, and
  // announcing a problem that resolves itself is its own kind of noise.
  const persistent = failure && failure.attempts >= 2 ? failure : null;

  const label = persistent
    ? FAILURE_LABELS[persistent.kind] ?? 'Cannot reach the server'
    : dot === 'ready'
      ? 'Synced'
      : dot === 'closed'
        ? 'Disconnected'
        : connectionState === 'reconnecting'
          ? 'Reconnecting…'
          : 'Syncing…';

  return (
    <>
      {/*
       * The dot and the words are one element (ADR-0163).
       *
       * They were two siblings of the bar until a cover could run up under it.
       * Over a photograph each control needs an opaque ground of its own, and a
       * dot with its own little pill beside a separate pill of words is two
       * chips where there is one fact. The wrapper is the chip; on a plain bar
       * it draws nothing.
       */}
      <span className="status-chip">
        <span
          className="status-dot"
          data-state={persistent ? 'closed' : dot}
          aria-hidden="true"
        />
        <span
          // status-text so the bar can truncate it. A long status — "Cannot reach
          // the sync server" — wrapped onto three lines and took the row's height
          // with it, so losing the connection also rearranged the page.
          className={`status-text ${persistent ? 'error' : 'muted'}`}
          style={{ fontSize: '0.85rem' }}
          title={persistent ? FAILURE_DETAIL[persistent.kind] : label}
        >
          {label}
        </span>
      </span>
      {peers.length > 0 && (
        <div className="peers" aria-label={t('page.otherPeopleHere', { count: peers.length })}>
          {peers.slice(0, 5).map((peer, i) => (
            <span
              key={i}
              className="peer"
              style={{ background: peer.color || 'var(--sone-accent)' }}
              title={peer.displayName}
            >
              {(peer.displayName || '?').slice(0, 1).toUpperCase()}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * What to call each failure.
 *
 * Short enough for a status line. The detail below carries the part an operator
 * needs, on hover and in the console — a status line is not the place for a
 * paragraph, but hiding the cause entirely is what made this hard to diagnose in
 * the first place.
 */
const FAILURE_LABELS: Record<string, string> = {
  unreachable: 'Cannot reach the sync server',
  no_auth_response: 'The server is not responding',
  closed: 'Connection lost',
};

const FAILURE_DETAIL: Record<string, string> = {
  unreachable:
    'The WebSocket connection to /sync never completed. This is usually a ' +
    'reverse proxy that does not forward WebSocket upgrades. Edits are kept ' +
    'locally and will sync once the connection works.',
  no_auth_response:
    'The connection opened but the server did not answer. Edits are kept ' +
    'locally in the meantime.',
  closed: 'The connection dropped and is being retried. Edits are kept locally.',
};
